/**
 * CandidateProcessor - Level 3 Manager
 *
 * RESPONSIBILITY:
 * Universal processing pipeline for all candidates, managing the two-phase processing flow.
 *
 * HIERARCHY:
 * - Reports to: ConfigurationProcessor (Level 2)
 * - Manages: Processing engines (Level 4)
 * - Peers: ShapeSearchManager, ConfigurationOptimizer
 *
 * KEY OPERATIONS:
 * - processCandidate: Single candidate processing with adjustments
 * - processCandidates: Batch processing for multiple candidates
 * - performCoreDistribution: Core financial calculations
 * - applyCandidateAdjustments: Targeted bond and other adjustments
 *
 * PROCESSING PHASES:
 * Phase 1: Core distribution (always happens)
 * - Revenue distribution with backwards calculation
 * - Cash flow calculation and DRF validation
 * - Results evaluation
 *
 * Phase 2: Candidate-level adjustments (conditional)
 * - Applied during shape search phases (first-fit, best-fit)
 * - Currently supports targeted bond adjustments
 *
 * NAVIGATION:
 * - For shape search, see: ./shape-search-manager.ts
 * - For optimization, see: ./optimizers/configuration-optimizer.ts
 * - For distribution engine, see: ../engines/distribution/
 */

import { BondSeriesBuilder } from '~/builders/bond-series/BondSeriesBuilder';
import { CashFlowsBuilder } from '~/builders/cash-flows/CashFlowsBuilder';
import { RevenueMapBuilder } from '~/builders/revenue-map/RevenueMapBuilder';
import { ProcessingFinalizer } from '~/engine/processing/engines/cashflow-engine/processing-finalizer';
import { RevenueDistributor } from '~/engine/processing/engines/revenue-engine/revenue-calculator';
import { StateManager } from '~/engine/processing/shared/state-manager';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import { TargetType } from '~/types';
import { TargetedBondAdjuster } from '../engines/adjustment-engine/targeted-bond-adjuster';
import type { ProcessingMetrics, ProcessingResult } from '../types';

// Import adjusters

export class CandidateProcessor {
  /**
   * Universal entry point for processing any candidate
   *
   * This function handles:
   * 1. Core distribution (always happens)
   * 2. Candidate-level adjustments (when applicable)
   *
   * All candidate processing MUST go through this function.
   *
   * @param candidate - The candidate to process
   * @param project - Project data (required for accessing date records, taxable values, etc.)
   * @param configuration - Configuration data (required for accessing bond series, DRF inputs)
   */
  static processCandidate(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): ProcessingResult {
    const startTime = Date.now();

    const phaseDisplay = candidate.createdInPhase
      ? candidate.createdInPhase.toUpperCase()
      : 'PROCESSING';
    logger.L3.debug(`🔄 ${phaseDisplay}: Processing candidate ${candidate.name}`);

    try {
      // Validate candidate state before processing
      const validation = StateManager.validateCandidateForProcessing(
        candidate,
        project,
        configuration
      );
      if (!validation.isValid) {
        throw new Error(`Invalid candidate state: ${validation.issues.join(', ')}`);
      }

      // Phase 1: Core distribution (always happens)
      let result = CandidateProcessor.performCoreDistribution(candidate, project, configuration);

      // Phase 2: Candidate-level adjustments (conditional based on context)
      if (CandidateProcessor.shouldApplyCandidateAdjustments(candidate, configuration)) {
        result = CandidateProcessor.applyCandidateAdjustments(result, configuration);
      }

      // Mark as processed
      candidate.isProcessed = true;

      // Calculate processing metrics
      const processingTime = Date.now() - startTime;
      const metrics: ProcessingMetrics = {
        processingTime,
        phase: candidate.createdInPhase || 'unknown',
        wasReprocessed: false,
        resetOperations: undefined,
      };

      // Logging behavior is governed by global logger configuration

      return {
        ...result,
        metrics,
      };
    } catch (error) {
      return StateManager.handleProcessingFailure(candidate, error);
    }
  }

  /**
   * Process multiple candidates using the same context
   * Useful for batch processing first-fit candidates, etc.
   */
  static processCandidates(
    candidates: Candidate[],
    project: Project,
    configuration: Configuration
  ): ProcessingResult[] {
    return candidates.map((candidate) =>
      CandidateProcessor.processCandidate(candidate, project, configuration)
    );
  }

  /**
   * Determines if candidate-level adjustments should be applied
   */
  private static shouldApplyCandidateAdjustments(
    candidate: Candidate,
    _configuration: Configuration
  ): boolean {
    // Apply adjustments during shape search phases
    return candidate.createdInPhase === 'first-fit' || candidate.createdInPhase === 'best-fit';
  }

  /**
   * Applies candidate-level adjustments based on bond characteristics
   */
  private static applyCandidateAdjustments(
    result: ProcessingResult,
    configuration: Configuration
  ): ProcessingResult {
    const candidate = result.candidate;

    // Check for targeted bonds (TargetType.DOES_NOT_EXCEED)
    if (CandidateProcessor.candidateHasTargetedBonds(candidate, configuration)) {
      logger.L3.debug(`🎯 Applying targeted bond adjustments for ${candidate.name}`);
      return TargetedBondAdjuster.adjust(candidate, result, configuration);
    }

    // Future: Add other adjustment types here

    return result;
  }

  /**
   * Checks if a candidate has targeted bonds that need adjustment
   */
  private static candidateHasTargetedBonds(
    _candidate: Candidate,
    configuration: Configuration
  ): boolean {
    return configuration.bondSeries.some(
      (bs) => bs.targetInformation?.targetType === TargetType.DOES_NOT_EXCEED
    );
  }

  /**
   * Performs core distribution and finalization
   */
  private static performCoreDistribution(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): ProcessingResult {
    // Step 1: Revenue Distribution
    CandidateProcessor.distributeRevenue(candidate, project, configuration);

    // Step 2 & 3: Universal finalization (cash flows, tax year plan, results evaluation)
    // Skip five-year rule validation during initial processing - the optimizer will fix violations later
    const finalizationResult = ProcessingFinalizer.finalizeAfterProcessing(
      candidate,
      configuration,
      {
        phase: candidate.createdInPhase?.toUpperCase() || 'PROCESSING',
        skipFiveYearRuleValidation: true, // Let optimizer fix five-year violations
      }
    );

    if (!finalizationResult.success) {
      throw new Error(`Finalization failed: ${finalizationResult.error}`);
    }

    return {
      success: true,
      candidate,
    };
  }

  /**
   * Step 1: Distribute Revenue
   * Uses the actual revenue distribution logic from the ported prototype
   */
  private static distributeRevenue(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): void {
    // Initialize revenue map if not already done or if reset
    if (!candidate.revenueMap || Object.keys(candidate.revenueMap).length === 0) {
      RevenueMapBuilder.buildRevenueMap(candidate, project, configuration);
    }

    // Ensure all bond series have their payment maps built before processing
    CandidateProcessor.ensureBondPaymentMapsInitialized(candidate, project, configuration);

    // Initialize cash flows if not already done (needed for distribution)
    if (!candidate.cashFlows || candidate.cashFlows.length === 0) {
      try {
        CashFlowsBuilder.initializeCashFlows(candidate, project, configuration);
      } catch (error) {
        logger.error(
          `❌ Error initializing cash flows:`,
          error instanceof Error ? error : undefined
        );
        throw error;
      }
    }

    // Execute revenue distribution using PURE algorithm (Phase 3b)
    // Uses distribute() which handles debug capture callback when DebugCapture session is active
    try {
      RevenueDistributor.distribute(candidate, configuration);
    } catch (error) {
      logger.error(`❌ Error in revenue distribution:`, error instanceof Error ? error : undefined);
      throw error;
    }
  }

  /**
   * Utility: Get processing summary for a candidate
   * Useful for debugging and monitoring
   */
  static getProcessingSummary(candidate: Candidate): {
    name: string;
    isProcessed: boolean;
    isViable: boolean;
    hasRevenue: boolean;
    hasCashFlows: boolean;
    hasTaxYearPlan: boolean;
    totalRevenue: number;
    failureMessages: string[];
  } {
    return {
      name: candidate.name,
      isProcessed: candidate.isProcessed,
      isViable: candidate.results.isViable,
      hasRevenue: Object.keys(candidate.revenueMap).length > 0,
      hasCashFlows: candidate.cashFlows.length > 0,
      hasTaxYearPlan:
        (candidate.taxYearBasedProjectPlan && candidate.taxYearBasedProjectPlan.length > 0) ||
        false,
      totalRevenue: candidate.results.totalRevenue,
      failureMessages: candidate.results.failureMessages,
    };
  }

  /**
   * Ensures all bond series have their payment maps initialized
   * This is critical for revenue distribution to access actual interest rates
   */
  private static ensureBondPaymentMapsInitialized(
    _candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): void {
    const bondSeries = configuration.bondSeries;
    const dateRecordsMapByPaymentDate = project.dateElements?.dateRecordsMapByPaymentDate || {};

    for (const bond of bondSeries) {
      try {
        // Check if payment map already exists and is populated
        if (!bond.bondPaymentMap || Object.keys(bond.bondPaymentMap).length === 0) {
          // Build payment map with escalating interest rates
          bond.bondPaymentMap = BondSeriesBuilder.buildBondPaymentMap(
            bond,
            dateRecordsMapByPaymentDate
          );
        }
      } catch (error) {
        logger.error(
          `❌ Error building payment map for bond ${bond.name}:`,
          error instanceof Error ? error : undefined
        );
        throw error; // Re-throw to be caught by main processing error handler
      }
    }
  }
}
